<template>
	<BlockVisibilityObserver title="TRUST">
		<div class="kova-v2-trust">
			<!-- Under reduced motion the marquee becomes one static wrapping row (duplicate run
			     hidden, no clipping / mask) — see the media query below; the marquee root carries
			     the block class for it. The legacy marquee component itself is untouched. -->
			<LandingKovaMarquee class="kova-v2-trust__marquee">
				<template v-for="item in items" :key="item.icon">
					<span class="kova-v2-trust__item">
						<img alt="" class="kova-v2-trust__icon" height="42" :src="icons[item.icon]" width="42" />
						<span>{{ item.label }}</span>
					</span>
					<span aria-hidden="true" class="kova-v2-trust__dot">·</span>
				</template>
			</LandingKovaMarquee>
		</div>
	</BlockVisibilityObserver>
</template>

<script setup lang="ts">
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaMarquee from '@/modules/Landing/components/LandingKovaMarquee.vue'
import { LandingComponentProps } from '@/modules/Landing/instances/@types/landing-types'

import clinicians from '@/assets/kova/landing/trust-clinicians.svg'
import eligibility from '@/assets/kova/landing/trust-eligibility.svg'
import hipaa from '@/assets/kova/landing/trust-hipaa.svg'
import legitscript from '@/assets/kova/landing/trust-legitscript.svg'

defineOptions({ name: 'LandingKovaV2Trust' })

defineProps<LandingComponentProps.KovaV2Trust>()

const icons = { clinicians, eligibility, hipaa, legitscript }
</script>

<style lang="scss" scoped>
// Mobile (6357:60814): 40px block padding, 42px icon + 24px label, 20px apart, edge to edge.
.kova-v2-trust {
	position: relative;
	padding-block: var(--size-40);

	font-family: var(--kova-lp-font);
	font-weight: 500;

	color: var(--kova-lp-muted);
}

.kova-v2-trust__marquee {
	--kova-lp-marquee-gap: var(--size-20);
	--kova-lp-marquee-fade: 0px; // the 390 frame clips hard at its edges
}

.kova-v2-trust__item {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	gap: var(--size-6); // 6357:60815

	font-size: var(--size-24);
	line-height: var(--size-34); // 24px × 1.4 = 33.60px — exact size not in design system
	white-space: nowrap;
}

.kova-v2-trust__icon {
	display: block;
	width: var(--size-42);
	height: var(--size-42);
}

.kova-v2-trust__dot {
	display: flex;
	align-items: center;
	font-size: var(--size-34);
	line-height: var(--size-48); // 34px × 1.4 = 47.60px — exact size not in design system
}

// Desktop (6547:16911): 36px block padding, 34px labels, 28px apart, and 300px cream
// fades over both ends (6547:16940 / 16941: solid to 20%, then clear).
@media (min-width: 1024px) {
	.kova-v2-trust {
		padding-block: var(--size-36);

		&::before,
		&::after {
			content: '';
			position: absolute;
			top: 50%;
			z-index: 1;

			width: 300px;
			max-width: 25%;
			height: var(--size-120);

			background: linear-gradient(to right, var(--kova-lp-page) 20%, var(--kova-lp-page-clear));
			transform: translateY(-50%);
			pointer-events: none;
		}

		&::before {
			left: 0;
		}

		&::after {
			right: 0;
			transform: translateY(-50%) rotate(180deg);
		}
	}

	.kova-v2-trust__marquee {
		--kova-lp-marquee-gap: var(--size-28);
	}

	.kova-v2-trust__item {
		font-size: var(--size-34);
		line-height: var(--size-48); // 34px × 1.4 = 47.60px — exact size not in design system
	}
}

// Reduced motion: no ticker. One run, wrapping and centred, nothing clipped
// or faded, all four labels readable. The marquee's own `animation: none` alone would
// leave the strip clipped at the viewport edge.
@media (prefers-reduced-motion: reduce) {
	.kova-v2-trust {
		&::before,
		&::after {
			display: none;
		}
	}

	.kova-v2-trust__marquee {
		overflow: visible;
		mask-image: none;

		:deep(.kova-lp-marquee__track) {
			width: auto;
			animation: none;
		}

		// The run is a non-shrinking flex item of the track; give it the strip's width so
		// its items wrap into lines instead of running past the viewport.
		:deep(.kova-lp-marquee__run) {
			flex: 1 1 100%;
			flex-wrap: wrap;
			justify-content: center;
			min-width: 0;
			row-gap: var(--size-12);
			padding: 0 var(--size-16);
		}

		:deep(.kova-lp-marquee__run:nth-child(2)) {
			display: none;
		}
	}

	// The separator after the last label has nothing to separate once the row ends.
	.kova-v2-trust__dot:last-child {
		display: none;
	}
}
</style>
